import React, { useEffect, useState } from 'react';
import './SOS.css';
import { fetchAlerts, deleteAlert } from '../utils/api';

// ── Nearby officers (demo data matching our 35 on-duty officers) ──
const NEARBY_OFFICERS = [
  { id: 'n1', name: 'Sai Kiran', batonId: 'VJW-0001', unit: 'Police Constable', distance: '0.3 km', phone: '+91 98765 43001', color: '#3b82f6' },
];



// ── Dispatch Modal ──────────────────────────────────────────────────────────
const DispatchModal = ({ alert, onClose, onConfirm }) => {
  const [selectedOfficers, setSelectedOfficers] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentTo, setSentTo] = useState([]);

  const toggle = (id) => {
    setSelectedOfficers(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (selectedOfficers.length === 0) return;
    setSending(true);
    // Simulate network delay
    await new Promise(r => setTimeout(r, 1800));
    const names = NEARBY_OFFICERS
      .filter(o => selectedOfficers.includes(o.id))
      .map(o => o.name);
    setSentTo(names);
    setSending(false);
    setSent(true);
    // Auto close after showing success
    setTimeout(() => {
      onConfirm(alert._id);
      onClose();
    }, 2500);
  };

  

  const unitDotColor = (unit) => {
    if (unit === 'Police Constable')  return '#3b82f6';
    if (unit === 'Shakthi Team')    return '#ec4899';
    if (unit === 'Traffic Constable') return '#dbc052';
    return '#64748b';
  };

  return (
    <div className="dispatch-overlay" onClick={onClose}>
      <div className="dispatch-modal" onClick={e => e.stopPropagation()}>

        {/* Modal Header */}
        <div className="dm-header">
          <div className="dm-header-left">
            <span className="dm-icon">🚔</span>
            <div>
              <div className="dm-title">Dispatch Response</div>
              <div className="dm-subtitle">Select officers to notify</div>
            </div>
          </div>
          <button className="dm-close" onClick={onClose}>✕</button>
        </div>

        {!sent ? (
          <>
            {/* Incident Summary */}
            <div className="dm-incident-box">
              <div className="dm-incident-label">📍 Incident Details</div>
              <div className="dm-incident-row">
                <span className="dm-incident-key">Officer in Distress</span>
                <span className="dm-incident-val">{alert.officer}</span>
              </div>
              <div className="dm-incident-row">
                <span className="dm-incident-key">Location</span>
                <span className="dm-incident-val location-highlight">{alert.location || 'Unknown'}</span>
              </div>
              <div className="dm-incident-row">
                <span className="dm-incident-key">Baton ID</span>
                <span className="dm-incident-val mono">{alert.batonId}</span>
              </div>
              <div className="dm-incident-row">
                <span className="dm-incident-key">Type</span>
                <span className={`dm-type-badge ${alert.type === 'SOS' ? 'dm-badge-sos' : 'dm-badge-panic'}`}>
                  {alert.type === 'SOS' ? '🆘' : '🔔'} {alert.type}
                </span>
              </div>
            </div>

            {/* Message preview */}
            <div className="dm-message-preview">
              <div className="dm-message-label">📱 Message to be sent</div>
              <div className="dm-message-text">
                🚨 <strong>EMERGENCY DISPATCH</strong><br />
                Officer <strong>{alert.officer}</strong> has triggered {alert.type} at <strong>{alert.location || 'Unknown location'}</strong>.<br />
                Baton: <strong>{alert.batonId}</strong> | Please respond immediately.<br />
                — Vijayawada City Command Centre
              </div>
            </div>

            {/* Nearby Officers */}
            <div className="dm-officers-label">
              👮 Nearby On-Duty Officers
              <span className="dm-select-hint">
                {selectedOfficers.length > 0
                  ? `${selectedOfficers.length} selected`
                  : 'tap to select'}
              </span>
            </div>
            <div className="dm-officers-list">
              {NEARBY_OFFICERS.map(officer => (
                <div
                  key={officer.id}
                  className={`dm-officer-row ${selectedOfficers.includes(officer.id) ? 'dm-officer-selected' : ''}`}
                  onClick={() => toggle(officer.id)}
                >
                  <div className="dm-officer-checkbox">
                    {selectedOfficers.includes(officer.id) ? '✅' : '⬜'}
                  </div>
                  <span className="dm-unit-dot" style={{ background: unitDotColor(officer.unit) }} />
                  <div className="dm-officer-info">
                    <div className="dm-officer-name">{officer.name}</div>
                    <div className="dm-officer-meta">{officer.unit} · {officer.batonId}</div>
                  </div>
                  <div className="dm-officer-right">
                    <div className="dm-officer-dist">📍 {officer.distance}</div>
                    <div className="dm-officer-phone">{officer.phone}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="dm-footer">
              <button className="dm-cancel-btn" onClick={onClose}>Cancel</button>
              <button
                className={`dm-send-btn ${selectedOfficers.length === 0 ? 'dm-send-btn--disabled' : ''}`}
                onClick={handleSend}
                disabled={selectedOfficers.length === 0 || sending}
              >
                {sending
                  ? <><span className="dm-sending-spinner" /> Sending...</>
                  : <>📲 Send to {selectedOfficers.length || '—'} Officer{selectedOfficers.length !== 1 ? 's' : ''}</>
                }
              </button>
            </div>
          </>
        ) : (
          /* ── Success screen ── */
          <div className="dm-success">
            <div className="dm-success-icon">✅</div>
            <div className="dm-success-title">Dispatch Sent!</div>
            <div className="dm-success-sub">
              Location alert sent to:
            </div>
            <div className="dm-success-names">
              {sentTo.map(name => (
                <span key={name} className="dm-success-name-tag">{name}</span>
              ))}
            </div>
            <div className="dm-success-note">
              Officers have been notified and are en route to <strong>{alert.location}</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main SOS Page ───────────────────────────────────────────────────────────
const SOS = () => {
  const [sosAlerts, setSosAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispatchAlert, setDispatchAlert] = useState(null); // which alert is being dispatched

  const fetchSOS = async () => {
  try {
    const res = await fetchAlerts();
    const alerts = res.data.alerts || res.data || [];
    const filtered = alerts.filter(a => a.type === 'SOS' || a.type === 'Panic');
    // No demoAlerts fallback — show empty state if nothing comes back
    setSosAlerts(
      filtered.map(a => ({ ...a, status: a.status || 'active' }))
    );
  } catch (err) {
    console.error('Failed to fetch alerts:', err);
    setSosAlerts([]); // show empty state, don't fake data
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchSOS();
    const t = setInterval(fetchSOS, 10000);
    return () => clearInterval(t);
  }, []);

  const timeAgo = (date) => {
    const secs = Math.floor((Date.now() - new Date(date)) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    return `${Math.floor(secs / 3600)}h ago`;
  };

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
    });

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });

  const handleRespond = (id) => {
    // Open dispatch modal instead of just changing status
    const alert = sosAlerts.find(a => a._id === id);
    setDispatchAlert(alert);
  };

  const handleDispatchConfirm = (id) => {
    setSosAlerts(prev =>
      prev.map(a => a._id === id ? { ...a, status: 'responding' } : a)
    );
  };

  const handleResolve = (id) => {
    setSosAlerts(prev =>
      prev.map(a => a._id === id ? { ...a, status: 'resolved' } : a)
    );
  };

  const handleDelete = async (id) => {
  if (!window.confirm('Delete this alert permanently?')) return;
  try {
    await deleteAlert(id);
    setSosAlerts(prev => prev.filter(a => a._id !== id));
  } catch (err) {
    console.error('Delete failed:', err);
  }
};

  const active     = sosAlerts.filter(a => a.status === 'active');
  const responding = sosAlerts.filter(a => a.status === 'responding');
  const resolved   = sosAlerts.filter(a => a.status === 'resolved');

  return (
    <div className="sos-page">

      {/* Dispatch Modal */}
      {dispatchAlert && (
        <DispatchModal
          alert={dispatchAlert}
          onClose={() => setDispatchAlert(null)}
          onConfirm={handleDispatchConfirm}
        />
      )}

      {/* Header */}
      <div className="sos-page-header">
        <div className="sos-title-row">
          <div className="sos-title-icon">🆘</div>
          <div>
            <h2 className="sos-title">SOS Management</h2>
            <p className="sos-subtitle">Real-time emergency alerts from field personnel</p>
          </div>
        </div>
        <div className="sos-header-stats">
          <div className="sos-stat sos-stat-critical">
            <span className="sos-stat-num">{active.length}</span>
            <span className="sos-stat-label">Active</span>
          </div>
          <div className="sos-stat sos-stat-warn">
            <span className="sos-stat-num">{responding.length}</span>
            <span className="sos-stat-label">Responding</span>
          </div>
          <div className="sos-stat sos-stat-ok">
            <span className="sos-stat-num">{resolved.length}</span>
            <span className="sos-stat-label">Resolved</span>
          </div>
        </div>
      </div>

      {loading && (
        <div className="sos-loading">
          <div className="sos-spinner" />
          <span>Loading SOS alerts...</span>
        </div>
      )}

      {!loading && sosAlerts.length === 0 && (
        <div className="sos-empty">
          <div className="sos-empty-icon">✅</div>
          <div className="sos-empty-title">No Active SOS Alerts</div>
          <div className="sos-empty-sub">All personnel are safe. System is monitoring.</div>
        </div>
      )}

      {active.length > 0 && (
        <section className="sos-section">
          <div className="sos-section-label sos-section-active">
            <span className="pulse-dot" /> Active SOS / Panic
          </div>
          <div className="sos-cards">
            {active.map(alert => (
              <SOSCard key={alert._id} alert={alert} timeAgo={timeAgo}
                formatTime={formatTime} formatDate={formatDate}
                onRespond={handleRespond} onResolve={handleResolve}
                onDelete={handleDelete} />
            ))}
          </div>
        </section>
      )}

      {responding.length > 0 && (
        <section className="sos-section">
          <div className="sos-section-label sos-section-responding">
            🚔 Response Dispatched
          </div>
          <div className="sos-cards">
            {responding.map(alert => (
              <SOSCard key={alert._id} alert={alert} timeAgo={timeAgo}
                formatTime={formatTime} formatDate={formatDate}
                onRespond={handleRespond} onResolve={handleResolve}
                onDelete={handleDelete} />
            ))}
          </div>
        </section>
      )}

      {resolved.length > 0 && (
        <section className="sos-section">
          <div className="sos-section-label sos-section-resolved">✅ Resolved</div>
          <div className="sos-cards">
            {resolved.map(alert => (
              <SOSCard key={alert._id} alert={alert} timeAgo={timeAgo}
                formatTime={formatTime} formatDate={formatDate}
                onRespond={handleRespond} onResolve={handleResolve}
                onDelete={handleDelete} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

/* ── Individual SOS Card ── */
const SOSCard = ({ alert, timeAgo, formatTime, formatDate, onRespond, onResolve, onDelete }) => {
  const isSOS = alert.type === 'SOS';
  const isResolved = alert.status === 'resolved';
  const isResponding = alert.status === 'responding';

  return (
    <div className={`sos-card ${isSOS ? 'sos-card-sos' : 'sos-card-panic'} ${isResolved ? 'sos-card-resolved' : ''}`}>
      <div className="sos-card-top">
        <div className="sos-card-badge" data-type={alert.type}>
          {isSOS ? '🆘' : '🔔'} {alert.type}
        </div>
        <div className="sos-card-time-ago">{timeAgo(alert.createdAt)}</div>
      </div>
      <div className="sos-card-grid">
        <div className="sos-info-item">
          <span className="sos-info-label">👤 Officer</span>
          <span className="sos-info-value">{alert.officer || 'Unknown'}</span>
        </div>
        <div className="sos-info-item">
          <span className="sos-info-label">🪪 Baton ID</span>
          <span className="sos-info-value mono">{alert.batonId}</span>
        </div>
        <div className="sos-info-item">
          <span className="sos-info-label">📍 Location</span>
          <span className="sos-info-value">{alert.location || 'Unknown'}</span>
        </div>
        <div className="sos-info-item">
          <span className="sos-info-label">🕐 Time</span>
          <span className="sos-info-value">{formatTime(alert.createdAt)}</span>
        </div>
        <div className="sos-info-item">
          <span className="sos-info-label">📅 Date</span>
          <span className="sos-info-value">{formatDate(alert.createdAt)}</span>
        </div>
        <div className="sos-info-item">
          <span className="sos-info-label">⚡ Severity</span>
          <span className={`sos-severity sos-severity-${alert.severity}`}>{alert.severity}</span>
        </div>
      </div>
      <div className="sos-card-message">{alert.message}</div>
      <div className="sos-card-actions">
  {!isResolved && !isResponding && (
    <button className="sos-btn sos-btn-respond" onClick={() => onRespond(alert._id)}>
      🚔 Dispatch Response
    </button>
  )}
  {isResponding && (
    <button className="sos-btn sos-btn-resolving" disabled>
      🚔 Response Dispatched
    </button>
  )}
  {!isResolved && (
    <button className="sos-btn sos-btn-resolve" onClick={() => onResolve(alert._id)}>
      ✅ Mark Resolved
    </button>
  )}
  {isResolved && (
    <span className="sos-resolved-tag">✅ Resolved</span>
  )}
  {/* Delete button — always visible */}
  <button className="sos-btn sos-btn-delete" onClick={() => onDelete(alert._id)}>
    🗑️ Delete
  </button>
</div>
    </div>
  );
};

export default SOS;
